import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertTriangle, RefreshCw, WifiOff } from "lucide-react";
import { useTranslations } from "use-intl";
import { Button } from "@/components/ui/button";
import { Modal, ModalBody, ModalFooter, ModalHeader } from "@/components/ui/modal";
import {
  BACKEND_HEALTH_PROBE_PATH,
  isBackendUnavailableMessage,
  useBackendHealthStore,
} from "@/stores/useBackendHealthStore";

const RETRY_FEEDBACK_MS = 900;
const APP_ENTRY_PATH = "/";

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, ms));
}

async function probeBackend(signal: AbortSignal): Promise<boolean> {
  const response = await fetch(BACKEND_HEALTH_PROBE_PATH, {
    cache: "no-store",
    signal,
  });

  if (response.status !== 502) return true;

  const data = (await response.json().catch(() => null)) as { message?: string } | null;
  return !isBackendUnavailableMessage(data?.message);
}

function formatSeconds(value: number): string {
  return Math.max(0, Math.ceil(value / 1000)).toString();
}

export function BackendConnectionModal() {
  const t = useTranslations("common.backend_connection");
  const status = useBackendHealthStore((state) => state.status);
  const isChecking = useBackendHealthStore((state) => state.isChecking);
  const nextRetryAt = useBackendHealthStore((state) => state.nextRetryAt);
  const lastError = useBackendHealthStore((state) => state.lastError);
  const beginBackendCheck = useBackendHealthStore((state) => state.beginBackendCheck);
  const finishBackendCheck = useBackendHealthStore((state) => state.finishBackendCheck);
  const [now, setNow] = useState(() => Date.now());

  const isOffline = status === "offline";
  const remainingMs = useMemo(
    () => Math.max(0, (nextRetryAt ?? now) - now),
    [nextRetryAt, now],
  );

  const checkNow = useCallback(async () => {
    if (useBackendHealthStore.getState().isChecking) return;

    const startedAt = Date.now();
    beginBackendCheck();
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 10_000);
    let ok = false;

    try {
      ok = await probeBackend(controller.signal);
    } catch {
      ok = false;
    } finally {
      window.clearTimeout(timeout);
      const elapsed = Date.now() - startedAt;
      if (elapsed < RETRY_FEEDBACK_MS) {
        await sleep(RETRY_FEEDBACK_MS - elapsed);
      }

      finishBackendCheck(ok);
      if (ok) {
        window.location.replace(APP_ENTRY_PATH);
      }
      setNow(Date.now());
    }
  }, [beginBackendCheck, finishBackendCheck]);

  useEffect(() => {
    if (!isOffline) return undefined;
    const tick = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(tick);
  }, [isOffline]);

  useEffect(() => {
    if (!isOffline || isChecking) return undefined;
    const delay = Math.max(0, (nextRetryAt ?? Date.now()) - Date.now());
    const timer = window.setTimeout(() => {
      void checkNow();
    }, delay);
    return () => window.clearTimeout(timer);
  }, [checkNow, isChecking, isOffline, nextRetryAt]);

  return (
    <Modal
      isOpen={isOffline}
      onClose={() => undefined}
      size="md"
      aria-label={t("title")}
      closeOnEscape={false}
      closeOnOverlayClick={false}
      showCloseButton={false}
    >
      <ModalHeader className="flex items-center gap-3 pr-6">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-[var(--warning-bg)] text-[var(--warning)]">
          <WifiOff className="size-5" aria-hidden />
        </span>
        <div className="min-w-0">
          <h2 className="text-base font-semibold text-[var(--foreground)]">
            {t("title")}
          </h2>
          <p className="mt-1 text-sm text-[var(--muted-foreground)]">
            {t("subtitle")}
          </p>
        </div>
      </ModalHeader>
      <ModalBody className="space-y-4">
        <p className="text-sm leading-6 text-[var(--foreground)]">{t("body")}</p>

        <div className="flex items-start gap-2 rounded-lg border border-[var(--border)] bg-[var(--muted)] px-3 py-2 text-sm text-[var(--muted-foreground)]">
          <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" aria-hidden />
          <span className="min-w-0 break-words">
            {lastError || t("fallback_error")}
          </span>
        </div>

        <div className="rounded-lg bg-[var(--accent)] px-3 py-2 text-sm font-medium text-[var(--foreground)]">
          {isChecking
            ? t("checking")
            : t("retry_countdown", { seconds: formatSeconds(remainingMs) })}
        </div>
      </ModalBody>
      <ModalFooter>
        <Button
          type="button"
          variant="primary"
          onClick={() => void checkNow()}
          disabled={isChecking}
          startIcon={<RefreshCw className={isChecking ? "size-4 animate-spin" : "size-4"} />}
        >
          {isChecking ? t("retrying_now") : t("retry_now")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
